// `carriesSensitiveParam` is the redaction's own rule (query or fragment,
// plain or percent-encoded), so the guards and PostHog / Faro agree.
import { carriesSensitiveParam } from '@/utils/urlRedaction';

// One rule for "may a third-party tag load / fire on this location?", shared
// by the GTM mount guard (GTMGuarded.tsx) and the Meta pixel loader
// (CookieConsent.tsx via metaPixelGate.ts). A tag that loads while the URL
// still carries an emailed credential sends it to the vendor with its first
// pageview, before any page effect can strip it.

// Routes where the URL is expected to carry a single-use magic-link token.
// The verify pages strip the token via `replaceState` in a page-level
// useEffect, but GTM's auto-pageview fires synchronously on container load —
// BEFORE the useEffect can scrub the URL. The consentless GA4 ping (Consent
// Mode v2 default `analytics_storage: 'denied'`) still carries the page URL,
// and consented users get a full GA4 event including the URL. Either way the
// token leaks to Google (and the Meta pixel's PageView carries it to Meta).
//
// Mitigation: skip the tags on these routes entirely. Users always navigate
// AWAY from verify pages (they're terminal entry points), so a tag mounting
// fresh on the next route is acceptable. The cookie consent banner only
// renders inside `(consent-manger)` so a missing tag on these pages doesn't
// break consent flow.
//
// See #3866 for the audit; #3864 (PostHog before_send redaction) and #3865
// (opt-out replaceState) close the other two leak surfaces.
const TOKEN_BEARING_ROUTE_SUFFIXES: readonly string[] = [
  '/professional-directory/claim/verify',
  '/professional-directory/opt-out/verify',
  // /email-preferences and /unsubscribe accept a JWT magic-link `?token=…`.
  // Both strip it via replaceState on arrival (#3937, #12259), but GTM
  // would fire on the initial pageview BEFORE the strip and capture the
  // URL. Since #12257 that token is a read/write credential for the
  // address's subscription state, not just a page key. See #3866.
  '/email-preferences',
  '/unsubscribe',
  // /org-invitation: receives ?token= from org-invite emails. Token is
  // decoded server-side but remains in document.location.href when GTM
  // fires client-side.
  '/org-invitation',
  // Newsletter double opt-in (#12323): the confirmation email's `#token=` is
  // a signed consent credential. The page strips it on mount, but a tag
  // would fire on the initial pageview first. Both locale slugs of the route.
  '/confirm-subscription',
  '/potvrdenie-odberu',
];

/**
 * Fired on `window` by code that removes a credential from the address bar
 * with `history.replaceState` (which raises no event of its own), so the
 * guards re-check at once. See `useLocationTail`.
 */
export const LOCATION_SCRUBBED_EVENT = 'lexomat:location-scrubbed';

export function announceLocationScrubbed(): void {
  window.dispatchEvent(new Event(LOCATION_SCRUBBED_EVENT));
}

/** The query string plus the fragment of the current location ('' on the server). */
export function currentLocationTail(): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.search}${window.location.hash}`;
}

/** The fragment of the current location ('' on the server). */
export function currentLocationHash(): string {
  return typeof window === 'undefined' ? '' : window.location.hash;
}

function isTokenBearingRoute(pathname: string | null): boolean {
  return TOKEN_BEARING_ROUTE_SUFFIXES.some((suffix) =>
    pathname?.endsWith(suffix)
  );
}

/**
 * May a third-party tag load or fire here? Not on a token-bearing route, and
 * not while the location still carries a sensitive param (catches a token
 * page nobody listed; the suffix list stays as belt and braces).
 */
export function isTrackingSafeLocation(
  pathname: string | null,
  tail: string = currentLocationTail()
): boolean {
  return !isTokenBearingRoute(pathname) && !carriesSensitiveParam(tail);
}
